import { Icon } from '@design-system/index'
import type { StyleProps } from '@design-system/types'
import styled from '@emotion/styled'
import { s3URL } from '@mathflat/common/util'
import type { SchoolTestPdfOption } from '@mathflat/domain/MakeCustomWorkbook'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import clsx from 'clsx'
import { debounce } from 'lodash-es'
import { observer, useLocalObservable } from 'mobx-react'
import { nanoid } from 'nanoid'
import type { PropsWithChildren } from 'react'
import {
  forwardRef,
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'

import { default as Checkbox } from '@/component/atoms/Checkbox/_Checkbox'
import { default as Divider } from '@/component/atoms/Divider'
import Loader from '@/component/atoms/Loader/Loader'
import { Toggle } from '@/component/atoms/Toggle'
import { AiAnalysisBanner } from '@/component/molecules/AiAnalysisBanner/AiAnalysisBanner'
import { Chip } from '@/component/molecules/Chip'
import { Dropdown, DropdownItem } from '@/component/molecules/Dropdown'
import { default as IconTextButton } from '@/component/molecules/IconTextButton'
import { default as Input } from '@/component/molecules/Input'
import { default as PlusMinusInput } from '@/component/molecules/PlusMinusInput'
import { Radio } from '@/component/molecules/Radio'
import { default as Tooltip } from '@/component/molecules/Tooltip'
import type { Student } from '@/dto'
import {
  BaseMakeCustomWorkbookContext,
  type RawMakeChapterT,
} from '@/page/MakeCustomWorkbook/context/abstract/baseMakeCustomWorkbookContext'
import modalService from '@/service/modal.service'
import type { Filter } from '@/types/lessonMaterialFilter'
import type { MakeWorksheetEqualityLevel, PreviousExclusionScope } from '@/types/makeWorksheet'

import AutoScoringToggle from './AutoScoringToggle'
import ExcludePreviousOption from './ExcludePreviousOption'
import S from './MakeLessonMaterialFilterItem.style'

// defaultPairX는 교재가 가질 수 있는 pairX값이고, 만약 그 값이 없다면 defaultFilter의 값을 바라본다.
// 이 값은 교재가 가질 수 있는 pairX의 값의 갯수를 결정한다.
// pairX는 실제 유저가 선택한 현재 필터의 값을 바라본다.
function 학습지교재구성({
  type,
  pairX,
  defaultPairX,
  similarX,
  similarLevel,
  onUpdateFilter,
  isSecondarySecuringType = false, // 2군교재여부
}: {
  type: 'WORKBOOK' | 'WORKSHEET'
  defaultPairX: Filter.PairX['pairX']
  pairX: Filter.PairX['pairX']
  similarX: Filter.SimilarX['similarX']
  similarLevel: Filter.SimilarLevel['similarLevel']
  onUpdateFilter: (params: Partial<Filter.PairX & Filter.SimilarX & Filter.SimilarLevel>) => void
  isSecondarySecuringType?: boolean
}) {
  const store = useLocalObservable(() => ({
    pairXDropdownCount: defaultPairX,
  }))

  return (
    <S.학습지교재구성>
      <strong>{type === 'WORKSHEET' ? '학습지' : '교재'} 구성</strong>
      <div className="gray-box">
        <span className="text-block">대상 문제의</span>
        {pairX !== null && (
          <>
            <span className="text-block">
              <strong className="primary-color">쌍둥이 문제</strong>
              <Dropdown
                className="select-option"
                width={100}
                value={pairX}
                onChange={(쌍둥이문제배수) => onUpdateFilter({ pairX: 쌍둥이문제배수 })}
              >
                <DropdownItem value={0}>0</DropdownItem>
                {store.pairXDropdownCount &&
                  new Array(store.pairXDropdownCount).fill(null).map((_, i) => {
                    return (
                      <DropdownItem key={i + 1} value={i + 1}>
                        {i + 1}
                      </DropdownItem>
                    )
                  })}
              </Dropdown>
              <span>개와</span>
            </span>
          </>
        )}
        <span className="text-block">
          <strong className="primary-color">유사문제</strong>
          <Dropdown
            className="select-option"
            width={100}
            value={similarX}
            onChange={(유사문제배수) => {
              onUpdateFilter({ similarX: 유사문제배수 })
            }}
          >
            {pairX !== null && <DropdownItem value={0}>0</DropdownItem>}
            <DropdownItem value={1}>1</DropdownItem>
            <DropdownItem value={2}>2</DropdownItem>
            <DropdownItem value={3}>3</DropdownItem>
          </Dropdown>
          <span>개로 {type === 'WORKSHEET' ? '학습지' : '교재'}를 만듭니다.</span>
        </span>
        {similarX !== null && similarX > 0 && (
          <span className="text-block">
            <span>유사문제 난이도는 </span>
            <Dropdown
              className="select-option"
              width={101}
              value={similarLevel}
              onChange={(유사문제난이도) => onUpdateFilter({ similarLevel: 유사문제난이도 })}
            >
              <DropdownItem value="EASIER">더 쉽게</DropdownItem>
              <DropdownItem value="AS_IS">그대로</DropdownItem>
              <DropdownItem value="HARDER">더 어렵게</DropdownItem>
            </Dropdown>
            <span>{tJosa('출제')}합니다.</span>
          </span>
        )}
      </div>
      {isSecondarySecuringType && <AiAnalysisBanner.SecondarySecuringTypeWorkbook />}
    </S.학습지교재구성>
  )
}

// 타입 못가져옴.. 너무 이상하게 달라서
function 모의고사({
  practiceTestOption,
  onUpdateFilter,
}: {
  practiceTestOption: Filter.PracticeTestOption['practiceTestOption']
  onUpdateFilter: (params: Filter.PracticeTestOption) => void
}) {
  return (
    <S.모의고사포함여부>
      <strong>모의고사 포함 여부</strong>
      <Radio.Group
        value={practiceTestOption}
        className="practice-test-radio-group"
        onChange={(event) => {
          onUpdateFilter({
            practiceTestOption: event.target
              .value as Filter.PracticeTestOption['practiceTestOption'],
          })
        }}
      >
        <Chip.Choice className="practice-test-radio-group-item" value="INCLUDE">
          모의고사 포함
        </Chip.Choice>
        <Chip.Choice className="practice-test-radio-group-item" value="EXCLUDE">
          모의고사 제외
        </Chip.Choice>

        <Chip.Choice className="practice-test-radio-group-item" value="ONLY">
          모의고사만
        </Chip.Choice>
      </Radio.Group>
    </S.모의고사포함여부>
  )
}

const ANSWER_TYPES = ['전체', '객관식', '주관식']
// 전체 필터를 보낼때는 서술형 값도 포함해서 보내야합니다. (객관식, 주관식값과는 별도로)
export const ANSWER_TYPE: Record<(typeof ANSWER_TYPES)[number], Filter.AnswerTypes['answerTypes']> =
  {
    객관식: ['SINGLE_CHOICE', 'MULTIPLE_CHOICE'],
    주관식: ['SHORT_ANSWER'],
    서술형: ['ESSAY'],
    get 전체() {
      return [...this.객관식, ...this.주관식, ...this.서술형]
    },
  }

function 답안타입({
  hasLimitedProblems,
  onlyAutoScorable,
  answerTypes,
  onUpdateFilter,
  showAutoScoringGuide = true,
}: {
  hasLimitedProblems?: boolean // 단원유형별에서만
  onlyAutoScorable?: Filter.AutoScorableOnly['autoScorableOnly']
  answerTypes: Filter.AnswerTypes['answerTypes']
  onUpdateFilter: (params: Partial<Filter.AnswerTypes & Filter.AutoScorableOnly>) => void
  showAutoScoringGuide?: boolean
}) {
  return (
    <S.문제타입>
      <div className="box">
        <div className="top">
          <strong>문제 타입</strong>
          {onlyAutoScorable !== undefined && (
            <AutoScoringToggle
              checked={onlyAutoScorable}
              onChange={(autoScorableOnly) => onUpdateFilter({ autoScorableOnly })}
              showGuide={showAutoScoringGuide}
            />
          )}
        </div>
        <Radio.Group
          value={
            answerTypes?.length === ANSWER_TYPE.전체.length
              ? '전체'
              : answerTypes?.includes('SINGLE_CHOICE')
                ? '객관식'
                : '주관식'
          }
          className="bottom problem-filter-type-radio-group"
          onChange={(event) => {
            const label = event.target.value as (typeof ANSWER_TYPES)[number]

            if (label) {
              onUpdateFilter({
                answerTypes: ANSWER_TYPE[label],
              })
            }
          }}
        >
          {ANSWER_TYPES.map((problemType) => (
            <Chip.Choice
              key={problemType}
              data-mathflat-unique-id={problemType}
              value={problemType}
              style={{ height: 40 }}
              className="problem-filter-type-radio-group-item"
            >
              {problemType}
            </Chip.Choice>
          ))}
        </Radio.Group>
        {onlyAutoScorable && hasLimitedProblems && (
          <div className="auto-scorable-info">
            <span>초등 1~3학년은 서술형 문제 비중이 높아</span>{' '}
            <span>‘자동 채점 ON’시 문제 수가 제한될 수 있습니다.</span>
          </div>
        )}
      </div>
    </S.문제타입>
  )
}

function 추가옵션({
  type,
  excludePrevious,
  excludeOutOfCurriculum,
  equalityLevel,
  limitProblemCountPerConcept,
  onUpdateFilter,
  showExcludePrevious = true,
  previousExclusionScope,
  studentIds,
  onChangeExcludePrevious,
}: {
  type: 'WORKSHEET' | 'WORKBOOK' | 'DAILY_LEARNING'
  excludePrevious: Filter.ExcludePrevious['excludePrevious']
  excludeOutOfCurriculum: Filter.ExcludeOutOfCurriculum['excludeOutOfCurriculum']
  equalityLevel?: MakeWorksheetEqualityLevel
  limitProblemCountPerConcept?: Filter.LimitProblemCountPerConcept['limitProblemCountPerConcept']
  onUpdateFilter: (
    params: Partial<
      Filter.ExcludePrevious &
        Filter.ExcludeOutOfCurriculum &
        Filter.LimitProblemCountPerConcept &
        Filter.문제수균등배분
    >,
  ) => void
  showExcludePrevious?: boolean
  previousExclusionScope?: PreviousExclusionScope | null
  studentIds?: Student['id'][] | null
  onChangeExcludePrevious?: (next: {
    excludePrevious: boolean
    previousExclusionScope?: PreviousExclusionScope
    studentIds?: Student['id'][]
  }) => void
}) {
  return (
    <S.추가옵션>
      <strong>추가 옵션</strong>
      {showExcludePrevious &&
        (type === 'DAILY_LEARNING' ? (
          <div className="additaionl-option-row">
            <Checkbox
              text={`기존 ${tJosa('출제')} 문제 제외`}
              checked={excludePrevious}
              onChange={(excludePrevious) => onUpdateFilter({ excludePrevious })}
            />
            <Tooltip
              iconType="info"
              Content={
                <>
                  최근 일주일 동안 생성했던
                  <br />
                  오늘의 학습에 포함된 문제들이 제외됩니다.
                  <br />
                  최소 10문제 미만이 될 경우 문제수를 채우기 위해
                  <br />
                  {tJosa('출제')}된 문제도 다시 {tJosa('출제')} 될 수 있습니다.
                </>
              }
              size={20}
              color="gray"
            />
          </div>
        ) : (
          onChangeExcludePrevious && (
            <ExcludePreviousOption
              previousExclusionScope={previousExclusionScope ?? undefined}
              studentIds={studentIds ?? undefined}
              onChange={onChangeExcludePrevious}
            />
          )
        ))}
      <div className="additaionl-option-row">
        <Checkbox
          className="exclude-ooc"
          text="교육 과정 외 문제 제외"
          checked={excludeOutOfCurriculum}
          onChange={(excludeOutOfCurriculum) => onUpdateFilter({ excludeOutOfCurriculum })}
        />
      </div>
      {limitProblemCountPerConcept !== undefined && (
        <div className="additaionl-option-row limit-problem-count-per-concept">
          <Checkbox
            text="유형별 최대 문제 수 제한"
            checked={limitProblemCountPerConcept !== null}
            onChange={(checked) => {
              onUpdateFilter({
                limitProblemCountPerConcept: checked ? 3 : null,
              })
            }}
          />
          <PlusMinusInput
            min={1}
            max={10}
            className="number-group"
            disabled={limitProblemCountPerConcept === null}
            value={limitProblemCountPerConcept || 3}
            onChange={(limitProblemCountPerConcept) => {
              onUpdateFilter({ limitProblemCountPerConcept })
            }}
          />
          <div className="text-with-tooltip">
            <span>개</span>
            <Tooltip
              iconType="info"
              size={20}
              color="gray"
              Content={
                <>
                  동일한 유형의 문제를 많이 {tJosa('출제')}하고 싶지 않을 때<br /> 1개의 유형에
                  포함될 문제 수를 제한할 수 있습니다.
                </>
              }
            />
          </div>
        </div>
      )}
      {type === 'DAILY_LEARNING' && (
        <div>
          <div className="additaionl-option-row">
            <Checkbox
              text="문제수 균등 배분"
              checked={!!equalityLevel}
              onChange={(equalityLevel) =>
                onUpdateFilter({ equalityLevel: equalityLevel ? 'CONCEPT' : null })
              }
            />
            <Tooltip
              iconType="info"
              Content={
                <>
                  단원/유형별로 문제 수가
                  <br /> 균등하게 배분됩니다.
                </>
              }
              size={20}
              color="gray"
            />
          </div>
          {equalityLevel && (
            <Radio.Group
              value={equalityLevel}
              className="equality-radio"
              onChange={(event) => {
                onUpdateFilter({
                  equalityLevel: event.target.value as Filter.문제수균등배분['equalityLevel'],
                })
              }}
            >
              <Radio value="BIG">대단원</Radio>
              <Radio value="MIDDLE">중단원</Radio>
              <Radio value="LITTLE">소단원</Radio>
              <Radio value="CONCEPT">유형별</Radio>
            </Radio.Group>
          )}
        </div>
      )}
    </S.추가옵션>
  )
}

function 학습단계추가토글({
  type,
  isOn,
  onChange,
}: {
  type: '단원유형별' | '시중교재'
  isOn: boolean
  onChange: (isOn: boolean) => void
}) {
  return (
    <S.학습단계추가>
      <div className="switch-wrapper">
        <label htmlFor="switch-make-workbook-type">학습단계 추가</label>
        <Toggle
          id="switch-make-workbook-type"
          checked={isOn}
          onClick={() => {
            onChange(!isOn)
          }}
        />
      </div>
      <p className="description">
        {isOn
          ? '학습단계를 추가해, 더 체계적인 교재를 만듭니다.'
          : '교재를 학습단계 없이 ' +
            (type === '단원유형별' ? '단원∙유형별로 ' : '') +
            '구성합니다.'}
      </p>
    </S.학습단계추가>
  )
}

function 문제수({
  value,
  onUpdateFilter,
  isSchoolTest = false,
}: {
  value: Filter.MaxProblemCount['maxProblemCount']
  onUpdateFilter: (value: Filter.MaxProblemCount['maxProblemCount']) => void
  isSchoolTest?: boolean
}) {
  const [_value, _setValue] = useState(String(value))

  const debouncedOnUpdateFilter = useCallback(debounce(onUpdateFilter, 300), [onUpdateFilter])

  const handleChange = (e) => {
    const num = Number(e.target.value)
    _setValue(e.target.value)
    debouncedOnUpdateFilter(num)
  }
  useEffect(() => {
    _setValue(String(value))
  }, [value])

  return (
    <S.문제수>
      <div className="top">
        <strong>{!isSchoolTest && '교재 전체 '}문제 수</strong>
        <sub className="sub">
          {isSchoolTest
            ? '기출 유형 + 교과서 문제'
            : `최대 ${BaseMakeCustomWorkbookContext.MAX_PROBLEM_COUNT} 문제`}
        </sub>
      </div>
      <div className="problem-count-option">
        <Radio.Group
          className="problem-count-radio-group"
          value={_value}
          onChange={(event) => onUpdateFilter(Number(event.target.value))}
        >
          <Chip.Choice value="200">200</Chip.Choice>
          <Chip.Choice value="400">400</Chip.Choice>
          <Chip.Choice value="600">600</Chip.Choice>
          <Chip.Choice value="800">800</Chip.Choice>
        </Radio.Group>
        <Divider size={40} margin={12} />
        <div className="problem-input">
          <Input
            data-testid="problem-count-input"
            name="problem-count"
            type="number"
            width="64px"
            height="40px"
            value={_value}
            onChange={handleChange}
          />
          <p>문제</p>
        </div>
      </div>
    </S.문제수>
  )
}

function 난이도({
  includeSteps = false,
  level,
  onUpdateFilter,
  onClickLevelSetting,
}: {
  includeSteps?: boolean
  level: Filter.Level['level']
  onUpdateFilter: (params: Partial<Filter.Level>) => void
  onClickLevelSetting?: () => void
}) {
  return (
    <S.난이도>
      <div className="level-header">
        <strong>{includeSteps && '학습 단계 '}난이도</strong>
        {onClickLevelSetting && (
          <IconTextButton
            className="with-icon"
            iconName="settings"
            onClick={onClickLevelSetting}
            size="small"
            text="난이도 설정"
          />
        )}
      </div>
      <Radio.Group
        className="level-radio-group"
        value={String(level)}
        onChange={(event) => {
          onUpdateFilter({
            level: Number(event.target.value) as Filter.Level['level'],
          })
        }}
      >
        <Chip.Choice value="1" className="level-radio-group-item">
          하
        </Chip.Choice>
        <Chip.Choice value="2" className="level-radio-group-item">
          중하
        </Chip.Choice>
        <Chip.Choice value="3" className="level-radio-group-item">
          중
        </Chip.Choice>
        <Chip.Choice value="4" className="level-radio-group-item">
          상
        </Chip.Choice>
        <Chip.Choice value="5" className="level-radio-group-item">
          최상
        </Chip.Choice>
      </Radio.Group>
    </S.난이도>
  )
}

function 단원구성({
  groupingChapterType,
  onUpdateFilter,
}: {
  groupingChapterType: Filter.GroupingChapterType['groupingChapterType']
  onUpdateFilter: (params: Partial<Filter.GroupingChapterType>) => void
}) {
  return (
    <S.단원구성>
      <strong>단원 구성</strong>

      <Radio.Group
        className="making-unit-radio-group"
        value={groupingChapterType}
        onChange={(event) =>
          onUpdateFilter({
            groupingChapterType: event.target
              .value as Filter.GroupingChapterType['groupingChapterType'],
          })
        }
      >
        <Radio value="LITTLE_CHAPTER" className="making-unit-radio-item">
          소단원 단위로 구성
        </Radio>
        <Radio value="MIDDLE_CHAPTER" className="making-unit-radio-item">
          중단원 단위로 구성
        </Radio>
      </Radio.Group>
    </S.단원구성>
  )
}

function 학교별기출PDF옵션({
  includeAnswerRateInPdf,
  includeLevelInPdf,
  includeTagTopInPdf,
  includeConceptNameInPdf,
  onUpdateFilter,
  allowedPdfOptions,
}: SchoolTestPdfOption & {
  onUpdateFilter: (params: Partial<SchoolTestPdfOption>) => void
  allowedPdfOptions: (keyof SchoolTestPdfOption)[]
}) {
  const store = useLocalObservable(() => ({
    includeAnswerRateInPdf,
    includeLevelInPdf,
    includeTagTopInPdf,
    includeConceptNameInPdf,
  }))

  return (
    <S.태그옵션>
      <strong className="header">
        <span>문제 영역 태그 옵션</span>
        <button
          onClick={() => {
            modalService.openModal(
              <S.태그옵션모달>
                <div className="header">
                  <strong>문제 영역 태그 옵션 예시</strong>
                  <div className="description">
                    구성별로 문제의 출처, 유형, 정답률, 난이도가 표기됩니다. 아래 예시를 통해 미리
                    확인해보세요.
                  </div>
                  <img
                    src={s3URL.mathflatAssetsBucket.teacher(
                      'images/make-workbook/problem-tag-option-example@3x.jpg',
                    )}
                    srcSet={`${s3URL.mathflatAssetsBucket.teacher(
                      'images/make-workbook/problem-tag-option-example@2x.jpg',
                    )} 2x, ${s3URL.mathflatAssetsBucket.teacher(
                      'images/make-workbook/problem-tag-option-example@3x.jpg',
                    )} 3x`}
                    width={812}
                    height={158}
                    alt="문제 영역 태그 옵션 예시 이미지"
                  />
                </div>
              </S.태그옵션모달>,
            )
          }}
        >
          <Icon name="info_fill" size={20} color="var(--Colors-Gray-500)" />
        </button>
      </strong>
      <div className="checkbox-group">
        <Checkbox
          text="유형명"
          checked={store.includeConceptNameInPdf}
          disabled={!allowedPdfOptions.includes('includeConceptNameInPdf')}
          onChange={(checked) => {
            store.includeConceptNameInPdf = checked
            onUpdateFilter({ includeConceptNameInPdf: checked })
          }}
        />
        <Checkbox
          text="정답률"
          checked={store.includeAnswerRateInPdf}
          disabled={!allowedPdfOptions.includes('includeAnswerRateInPdf')}
          onChange={(checked) => {
            store.includeAnswerRateInPdf = checked
            onUpdateFilter({ includeAnswerRateInPdf: checked })
          }}
        />
        <Checkbox
          text="난이도"
          checked={store.includeLevelInPdf}
          disabled={!allowedPdfOptions.includes('includeLevelInPdf')}
          onChange={(checked) => {
            store.includeLevelInPdf = checked
            onUpdateFilter({ includeLevelInPdf: checked })
          }}
        />
        <Checkbox
          text="연관 문항 정보"
          checked={store.includeTagTopInPdf}
          disabled={!allowedPdfOptions.includes('includeTagTopInPdf')}
          onChange={(checked) => {
            store.includeTagTopInPdf = checked
            onUpdateFilter({ includeTagTopInPdf: checked })
          }}
        />
      </div>
    </S.태그옵션>
  )
}

const 문제수결과 = ({
  problemCount,
  MainPrefixTextSlot,
  MainSuffixTextSlot,
  SubTextSlot,
  ErrorTextSlot,
  isError,
  isLoading,
}: {
  problemCount?: number | string
  MainPrefixTextSlot?: ReactNode
  MainSuffixTextSlot?: ReactNode
  SubTextSlot?: ReactNode
  ErrorTextSlot?: ReactNode
  isError?: boolean
  isLoading?: boolean
}) => {
  const problemCountText = useMemo(() => {
    if (isLoading) return <Loader width={24} />
    if (problemCount === undefined) return '-'
    if (isError && ErrorTextSlot === undefined) return '???'
    return problemCount
  }, [isLoading, problemCount, isError, ErrorTextSlot])

  return (
    <S.문제수표시 className={clsx(isError && 'error')}>
      <div className="top">
        <div className="main-text">
          {MainPrefixTextSlot}
          <span className="main-problem-count">
            <span className={clsx('primary-color', isError && 'error-color')}>
              {problemCountText}
            </span>{' '}
            개
          </span>
          {MainSuffixTextSlot}
        </div>
        {isError && <span className="error-text">{ErrorTextSlot}</span>}
      </div>
      {SubTextSlot && (
        <div className="bottom">
          <span className="sub-text">{SubTextSlot}</span>
        </div>
      )}
    </S.문제수표시>
  )
}

type LearningStep = Filter.CustomWorkbookSteps['steps'][number]
type LearningStepDescription = {
  step: LearningStep
  stepName: string
  subDescription: string
}

export const LEARNING_STEP_NAME: Record<
  Exclude<RawMakeChapterT['step'], null | undefined>,
  string
> = {
  THEORY: '개념 이해',
  EXERCISE: '유형 연습',
  CHALLENGE: '응용 도전',
  WRAP_UP: '단원 마무리',
  PAIR_SIMILAR: '기출 유형',
  SCHOOL_WORKBOOK: '교과서 문제',
  PRACTICE_TEST: '실전 모의고사',
}

const LEARNING_STEPS: LearningStepDescription[] = [
  {
    step: 'THEORY',
    stepName: LEARNING_STEP_NAME.THEORY,
    subDescription: '소단원별 개념 + 개념 기본 문제',
  },
  {
    step: 'EXERCISE',
    stepName: LEARNING_STEP_NAME.EXERCISE,
    subDescription: '유형별 개념 + 유형별 유제',
  },
  {
    step: 'CHALLENGE',
    stepName: LEARNING_STEP_NAME.CHALLENGE,
    subDescription: '중단원별 응용 문제',
  },
  {
    step: 'WRAP_UP',
    stepName: LEARNING_STEP_NAME.WRAP_UP,
    subDescription: '중단원 확인 문제',
  },
]

const calcRatioByStep = (steps: LearningStep[]): [LearningStep, number][] => {
  const TOTAL_STEP_RATIO = 1
  const EXERCISE_STEP_RATIO = 0.7

  let restCornerCountWithoutPattern = steps.length
  let restCornerRatioWithoutPattern = TOTAL_STEP_RATIO

  if (steps.includes('EXERCISE')) {
    restCornerRatioWithoutPattern -= EXERCISE_STEP_RATIO
    restCornerCountWithoutPattern -= 1
  }

  // 순서를 지키기 위해 corners대신 LEARNING_STEPS를 사용
  return LEARNING_STEPS.filter((corner) => steps.includes(corner.step)).map(
    ({ step: cornerType }) => {
      // 1개인 경우 무조건 100%
      if (steps.length === 1) {
        return [cornerType, TOTAL_STEP_RATIO]
      }

      // 1개가 아니면서 유형 연습은 무조건 70%
      if (cornerType === 'EXERCISE') {
        return [cornerType, EXERCISE_STEP_RATIO]
      }

      // 1개가 아니면서 나머지 단계는 균등분할
      return [
        cornerType,
        Number((restCornerRatioWithoutPattern / restCornerCountWithoutPattern).toFixed(2)),
      ]
    },
  )
}

interface Props extends StyleProps {
  steps: LearningStep[]
  onChange: (step: LearningStep) => void
}

const 학습단계 = ({ steps, onChange, className }: Props) => {
  const ratio = calcRatioByStep(steps)

  return (
    <S.학습단계 className={className}>
      <header>
        <h3>
          <strong>학습 단계 구성</strong>
        </h3>
      </header>
      <div className="section-composition">
        <div
          className="composition-bar-wrapper"
          style={{
            gridTemplateColumns: ratio.map(([_, r]) => r + 'fr').join(' '),
          }}
        >
          {ratio.map(([corner, r]) => {
            return (
              <div key={nanoid()}>
                <CompositionBar step={corner} />
                <div>{r * 100}%</div>
              </div>
            )
          })}
        </div>
        <div className="section-composition__description">
          <p>
            <strong>선택한 단원, 난이도, 학습단계 설정</strong>에 따라 문제 비율이 자동 분배됩니다.
          </p>
        </div>
      </div>
      <div className="composition-table">
        <div className="table-header">설정</div>
        <div className="table-header">학습단계 설명</div>
        {LEARNING_STEPS.map(({ step: cornerType, stepName: cornerName, subDescription }, index) => {
          const step = index + 1

          return (
            <Fragment key={cornerType}>
              <div className="table-column">
                <label>
                  <div className="gap4">
                    <StepChip step={cornerType} />
                    <span>{step}단계</span>
                  </div>
                  <Toggle
                    checked={steps.includes(cornerType)}
                    onClick={() => onChange(cornerType)}
                  />
                </label>
              </div>
              <div className="table-column">
                <span>{cornerName}</span>
                <span>{subDescription}</span>
              </div>
            </Fragment>
          )
        })}
        <div className="table-footer">
          <div className="gap8">
            <StepChip step="THEORY" />
            소단원별 반복
          </div>
          <div className="gap8">
            <div className="gap4">
              <StepChip step="EXERCISE" />
              <StepChip step="CHALLENGE" />
              <StepChip step="WRAP_UP" />
            </div>
            중단원별 반복
          </div>
        </div>
      </div>
    </S.학습단계>
  )
}

interface StepChipProps {
  step: LearningStep
}

const mapStepToColor = (step: LearningStep) => {
  switch (step) {
    case 'THEORY':
      return 'var(--Colors-Indigo-400,#818CF8)'
    case 'EXERCISE':
      return 'var(--Colors-Indigo-600,#4F46E5)'
    case 'CHALLENGE':
      return 'var(--Colors-Indigo-800,#3730A3)'
    case 'WRAP_UP':
      return 'var(--Colors-Indigo-950,#1E1B4B)'
  }
}

const CompositionBar = styled.div<StepChipProps>`
  width: 100%;
  height: 12px;
  border-radius: var(--Radius-100);
  background-color: ${({ step: corner }) => mapStepToColor(corner)};
`

const StepChip = styled.div<StepChipProps>`
  width: 8px;
  height: 8px;
  border-radius: var(--Radius-100);
  background-color: ${({ step: corner }) => mapStepToColor(corner)};
`

const ItemContainer = forwardRef<Scrollbars, PropsWithChildren>(({ children }, ref) => {
  return (
    <Scrollbars
      ref={ref}
      className="make-lesson-material-filter-item-container-scollbars"
      autoHide
      autoHideDuration={3000}
    >
      <S.ItemContainer>{children}</S.ItemContainer>
    </Scrollbars>
  )
})

ItemContainer.displayName = 'ItemContainer'

const MakeLessonMaterialFilterItem = {
  학습지교재구성: observer(학습지교재구성),
  모의고사: observer(모의고사),
  답안타입: observer(답안타입),
  추가옵션: observer(추가옵션),
  학습단계추가토글: observer(학습단계추가토글),
  난이도: observer(난이도),
  문제수: observer(문제수),
  단원구성: observer(단원구성),
  학교별기출PDF옵션: observer(학교별기출PDF옵션),
  문제수결과: observer(문제수결과),
  Container: observer(ItemContainer),
  학습단계: observer(학습단계),
}

export default MakeLessonMaterialFilterItem
